import { MouseEvent, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { AdminUser } from '../../types/auth'
import { Badge } from '../../components/ui/Badge'
import { PlanBadge } from './PlanBadge'
import { UserActionButtons } from './UserActionButtons'
import { UserActions } from './useUserActions'
import { TR, TD, ActionsCell } from '../../components/ui/Table'
import { tokens } from '../../styles/tokens'

interface Props {
  item: AdminUser
  actions: UserActions
}

const ClickableRow = styled(TR)`
  cursor: pointer;
`

const NameLink = styled(Link)`
  text-decoration: none;

  &:hover p:first-child {
    text-decoration: underline;
  }
`

const Avatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: white;
  font-weight: 500;
  flex-shrink: 0;
`

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const UserName = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${tokens.purple900};
  line-height: 1.3;
`

const UserEmail = styled.p`
  font-size: 11px;
  color: ${tokens.purple400};
`

const IntegrationDot = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => (p.$active ? '#3b6d11' : tokens.purple100)};
`

const IntegrationsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const IntegrationLabel = styled.span`
  font-size: 11px;
  color: ${tokens.purple400};
`

const ActionsMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
`

const StatusDot = styled.div<{ $active: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => (p.$active ? '#639922' : '#e24b4a')};
  flex-shrink: 0;
`

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${tokens.purple400};
`

export function UserRow({ item, actions }: Props) {
  const navigate = useNavigate()
  // Whole row opens the detail page; the name is also a real link (new tab, keyboard)
  const handleOpen = useCallback(() => navigate(`/users/${item.id}`), [navigate, item.id])
  const stop = useCallback((e: MouseEvent) => e.stopPropagation(), [])

  const initials = item.name
    ? item.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : item.email[0].toUpperCase()

  return (
    <ClickableRow onClick={handleOpen}>
      <TD>
        <NameLink to={`/users/${item.id}`} onClick={stop}>
          <UserCell>
            <Avatar>{initials}</Avatar>
            <div>
              <UserName>{item.name ?? '—'}</UserName>
              <UserEmail>{item.email}</UserEmail>
            </div>
          </UserCell>
        </NameLink>
      </TD>

      <TD>
        <PlanBadge tier={item.subscription?.tier} />
      </TD>

      <TD>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Badge color={item.isAdmin ? 'purple' : 'gray'}>{item.isAdmin ? 'Admin' : 'User'}</Badge>
          {item.mustResetPassword && <Badge color="amber">Must reset pw</Badge>}
          {!item.emailVerified && <Badge color="red">Unverified</Badge>}
          {item.supportRestricted && <Badge color="red">Support blocked</Badge>}
        </div>
      </TD>

      <TD>
        <StatusCell>
          <StatusDot $active={item.active} />
          {item.active ? 'Active' : 'Inactive'}
        </StatusCell>
      </TD>

      <TD>
        <IntegrationsCell>
          <IntegrationDot $active={item.integrations.google} />
          <IntegrationLabel>G</IntegrationLabel>
          <IntegrationDot $active={item.integrations.apple} />
          <IntegrationLabel>A</IntegrationLabel>
        </IntegrationsCell>
      </TD>

      <TD style={{ fontSize: 12, color: tokens.purple400 }}>
        {item.lastLoginAt ? new Date(item.lastLoginAt).toLocaleDateString() : '—'}
      </TD>

      <ActionsCell onClick={stop}>
        <ActionsMenu>
          <UserActionButtons item={item} actions={actions} />
        </ActionsMenu>
      </ActionsCell>
    </ClickableRow>
  )
}
